<template>
  <neo-layout :loading="isFormLoading" class="h-full overflow-hidden">
    <div class="w-full max-h-full overflow-auto scroll-bar p-1 h-screen">
      <slot></slot>
      <div v-if="getIsCheckToolInited && formData">
        <FormBuilder
          v-if="formData?.sections?.length"
          ref="check-form-builder"
          :key="check_id"
          :data="formData"
          :actionsNames="fbBtnsLabels"
          :showPreviewButton="false"
          :singleSectionView="true"
          :isDBSCheck="integratedCheckSubType === 'dbs'"
          :permissionEnforced="'case.read_write_check_screen'"
          :saveSectionMethod="saveSectionMethod"
          @submit="submitForm"
        >
          <template v-if="isRedirectionScreen" #navigation="slotProps">
            <FormBuilderNavBar
              v-bind="slotProps"
              :hideActions="['back']"
              :disabled="disableSave"
              :show-back-button="showBackButton"
              @back="goBack"
            >
              <template #before-save>
                <div>
                  <slot name="form-builder-nav"/>
                </div>
              </template>
            </FormBuilderNavBar>
          </template>
        </FormBuilder>
        
        <UIContentBox v-else-if="isRedirectionScreen && !formData?.sections?.length" class="flex items-center justify-end gap-5 mt-2">
          <div class="flex justify-between gap-4 w-full items-center">
            <slot name="form-builder-nav" />
            <BackButton
              v-if="showBackButton"
              @click="goBack"
            />
            <Button
              class="ml-auto"
              type="primary"
              :text="$t('actions.save_and_next')"
              :disabled="disableSave"
              @click="$emit('submit')"
            />
          </div>
        </UIContentBox>
      </div>
    </div>
  </neo-layout>
</template>

<script>
import axios from "@/axios";
import { 
  formatMultipleSections,
  formatDataForFormPreview
} from "@shared/case-entities/utils";
import NeoLayout from "@shared/components/neo-layout";
import FormBuilder from "@shared/components/form-builder";
import FormBuilderNavBar from "@shared/case-creation/CaseCreationFlow/components/FormBuilderNavBar"
import { mapGetters, mapActions } from "vuex";
import Button from "@shared/components/button";
import { BackButton } from "@shared/case-creation/CaseCreationFlow/components";

export default {
  name: "integrated-check-form",
  
  components: {
    NeoLayout,
    FormBuilder,
    FormBuilderNavBar,
    Button,
    BackButton,
  },
  
  props: {
    caseId: {
      type: String,
      default: null
    },
    checkId: {
      type: String,
      default: null
    },
    entityId: {
      type: String,
      default: null
    },
    disabled: {
      type: Boolean,
      default: false
    },
    isRedirectionScreen: {
      type: Boolean,
      default: false
    },
    // forces the back button to be shown
    showBackButton: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      isFormLoading: true,
      formData: {},
      formBuilderActionNames: {
        submit: this.$t('form_builder.submit')
      }
    }
  },

  computed: {
    ...mapGetters([
      "getIsCheckToolInited",
      "getIsRedirectionScreen",
      "getHasMatches",
      "getNoMatchesFlag",
      "getTenantId",
      "getDomainCompanyDetailsSearchList",
      "getAdverseFilteredResults",
      "getSearchEngineResultList",
      "getUserFeatures"
    ]),

    fbBtnsLabels() {
      const tKey = this.getIsRedirectionScreen ? 'actions.save_and_next' : 'actions.submit';  
      return {
        saveNext: this.$t(tKey)
      };
    }, 
    disableSave() {
      return this.disabled || (this.getIsRedirectionScreen && !this.getHasMatches && !this.getNoMatchesFlag);
    }, 
    check_id() {
      return this.checkId || this.$route?.query?.check;
    },
    case_id() {
      return this.caseId || this.$route?.query?.caseid;
    },
    entity_id() {
      return this.entityId || this.$route?.query?.entityId;
    },
    integratedCheckSubType () {
      return 'false';
    },
  },

  created() {
    this.setRedirectionScreenFlag(this.isRedirectionScreen);
  },

  beforeDestroy() {
    this.setRedirectionScreenFlag(false);
  },

  async mounted() {
    await this.fetchCheckForm();
  },

  methods: {
    ...mapActions([
      "setRedirectionScreenFlag",
      "handleNoMatch"
    ]),

    async saveSectionMethod(payload) {
      if (!this.formData?.sections?.length) return;

      try {
        this.isFormLoading = true;
        const formattedPayload  = formatMultipleSections(payload.data);

        const url = this.getIsRedirectionScreen
          ? `case/${this.case_id}/entity/${this.entity_id}/case-entity-creation-form`
          : `/case/form/${this.case_id}/entity/${this.entity_id}`;
        const response = await axios.post(url, formattedPayload);


        this.$emit('submit')

        if (response) {
          this.$toast.success(this.$t('general.form_submitted'));
        }
      } catch (error) {
        this.$toast.error(this.$t('general.default_error2'));
        throw error;
      } finally {
        this.isFormLoading = false;
      }
    },

    async submitForm() {
      this.isFormLoading = true;

      try {
        await axios.post(`case/${this.case_id}/entity/${this.entity_id}/tenant-check/${this.check_id}/form/submit`, {
          params: {
            ...(this.isRedirectionScreen ? { case_creation: true } : {})
          }
        });
      } catch (error) {
        this.$toast.error(error.response.data.detail);
      } finally {
        this.isFormLoading = false;
      }
    },

    async fetchCheckForm() {
      this.isFormLoading = true;

      try {
        const url = this.getIsRedirectionScreen 
          ? `case/${this.case_id}/entity/${this.entity_id}/case-entity-creation-form-v2?tenant_check_id=${this.check_id}` 
          : `case/form/${this.case_id}/entity/${this.entity_id}?tenant_check_id=${this.check_id}`;
        const { data } = await axios(url);
        this.formData = formatDataForFormPreview(data);
      } catch (error) {
        console.error(error);
        this.$toast.error(error?.response?.data?.detail || this.$t("general.default_error"))
      } finally {
        this.isFormLoading = false;
      }
    },

    goBack() {
      this.$emit("back");
    },
  }
}
</script>
